import { KAI_SHIKI_TYPOGRAPHY } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { css, type CSSObject } from '@emotion/react';
import type { CSSProperties } from 'react';

export const PAGE_STYLE = css`
  display: flex;
  height: 100%;
  background: #e9e7e3;
  color: #1d1d1d;
  overflow: hidden;
`;

export const SIDEBAR_STYLE = css`
  width: 204px;
  background: #ffffff;
  border-right: 1px solid #e8e2d8;
  display: flex;
  flex-direction: column;
  padding: 18px 12px 20px;
  flex-shrink: 0;
  height: 100%;
  overflow: hidden;
`;

export const SIDEBAR_SECTION_TITLE_STYLE = css`
  ${KAI_SHIKI_TYPOGRAPHY.smallBold}
  color: #1d1d15;
  text-transform: uppercase;
  margin: 8px 10px 10px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  letter-spacing: 0.04em;
`;

export const SIDEBAR_ITEM_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-radius: 3px;
  padding: 10px 12px;
  cursor: pointer;
  color: #1d1d15;
  ${KAI_SHIKI_TYPOGRAPHY.medium}
`;

export const SIDEBAR_ITEM_ACTIVE_STYLE = css`
  ${SIDEBAR_ITEM_STYLE}
  background: #dfdfdf !important;
  background-color: #dfdfdf !important;
  color: #1d1d1d;
  font-weight: 700;
`;

export const SIDEBAR_ITEM_NAME_STYLE = css`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SIDEBAR_ITEM_COUNTS_STYLE = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const SIDEBAR_TOTAL_COUNT_STYLE = css`
  width: 19px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #1d1d15;
`;

export const ATTENTION_BADGE_STYLE = css`
  width: 6px;
  height: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #a01c1c;
  flex-shrink: 0;
`;

export const MAIN_STYLE = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #dfdfdf;
  overflow: hidden;
`;

export const CONTENT_STYLE = css`
  flex: 1;
  overflow-y: auto;
  background: #dfdfdf;
  padding: 18px 18px 28px;
`;

export const TOPBAR_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;

  @media (max-width: 760px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const OVERVIEW_TITLE_ROW_STYLE = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 4px;

  @media (max-width: 560px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

export const OVERVIEW_TITLE_STYLE = css`
  font-size: ${pxToRem(18)};
  font-weight: 500;
  color: #1d1d1d;
`;

export const OVERVIEW_DIVIDER_STYLE = css`
  width: 1px;
  height: 18px;
  background: #e0d9cf;
`;

export const OVERVIEW_SUBTITLE_STYLE = css`
  font-size: ${pxToRem(14)};
  color: #4e4a45;
  font-weight: 600;
`;

export const HEADER_COPY_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

export const DASHBOARD_TABS_STYLE = css`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 60px;
  margin-left: 10px;
`;

export const DASHBOARD_TAB_STYLE = css`
  border: none;
  background: transparent;
  padding: 0 4px 0 0;
  height: 24px;
  border-left: none;
  border-bottom: 2px solid transparent;
  line-height: 1;
  color: rgba(255, 255, 255, 0.62);
  font-size: ${pxToRem(12)};
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
`;

export const DASHBOARD_TAB_ACTIVE_STYLE = css`
  ${DASHBOARD_TAB_STYLE}
  color: #f5c826;
  border-bottom-color: #d5ab16;
`;

export const SEARCH_ACTIONS_STYLE = css`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 760px) {
    justify-content: stretch;
  }
`;

export const SEARCH_STYLE = css`
  position: relative;
  width: 320px;
  max-width: 100%;
  height: 53px;

  [data-kai-shiki-common-field-value="true"]::placeholder {
    color: #606060;
    font-size: ${pxToRem(12)};
    font-style: italic;
    line-height: 1.333;
    letter-spacing: 0.02em;
  }

  [data-kai-shiki-common-field-value="true"]::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
    display: none;
  }

  [data-kai-shiki-common-field-value="true"]::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }

  @media (max-width: 760px) {
    width: 100%;
  }
`;

export const SEARCH_COLLAPSED_BUTTON_STYLE = css`
  width: 38px;
  height: 38px;
  border: 1px solid #ddd6cb;
  border-radius: 4px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #1d1d1d;
  padding: 0;
`;

export const SEARCH_ICON_STYLE = css`
  width: 17px;
  height: 17px;
  color: #1d1d1d;
  flex-shrink: 0;
`;

export const SEARCH_FIELD_STYLE = {
  width: '100%',
  height: 53,
  background: 'transparent',
} satisfies CSSProperties;

export const SEARCH_FIELD_BOX_STYLE = {
  border: '1px solid #ddd6cb',
  borderRadius: 4,
  background: '#ffffff',
} satisfies CSSObject;

export const SEARCH_FIELD_LABEL_STYLE = {
  background: '#dfdfdf',
  color: '#606060',
} satisfies CSSObject;

export const SEARCH_FIELD_VALUE_STYLE = {
  marginLeft: 24,
  width: 'calc(100% - 34px)',
  fontSize: pxToRem(13),
  color: '#1d1d1d',
} satisfies CSSObject;

export const SEARCH_FIELD_ICON_STYLE = css`
  position: absolute;
  top: ${pxToRem(25)};
  left: 12px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const SEARCH_CLEAR_BUTTON_STYLE = css`
  position: absolute;
  top: 22px;
  right: 12px;
  z-index: 2;
  width: 16px;
  height: 16px;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  color: #1d1d1d;
  flex-shrink: 0;
`;

export const ACTION_BUTTON_STYLE = css`
  height: 40px;
  border: none;
  border-radius: 6px;
  padding: 0 18px;
  background: #45b06f;
  color: #ffffff;
  font-size: ${pxToRem(12)};
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
`;

export const SUMMARY_ROW_STYLE = css`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) 1px minmax(0, 1fr) 1px minmax(0, 1fr);
  align-items: stretch;
  gap: 16px;
  margin-bottom: 26px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const SUMMARY_DIVIDER_STYLE = css`
  width: 1px;
  background: #e2ddd3;

  @media (max-width: 980px) {
    display: none;
  }
`;

export const SUMMARY_CARD_STYLE = css`
  min-height: 80px;
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 3px;
  cursor: pointer;
  appearance: none;
  text-align: left;
  justify-items: start;
`;

export const SUMMARY_CARD_ACTIVE_STYLE = css`
  ${SUMMARY_CARD_STYLE}
  background: #f7f7f7;
  border: 1px solid #d7cfc1;
  box-shadow: 0 1px 0 rgba(29, 29, 29, 0.02);
`;

export const SUMMARY_CARD_INACTIVE_STYLE = css`
  ${SUMMARY_CARD_STYLE}
  background: transparent;
  border: 1px solid transparent;
  color: #a0a0a0;
`;

export const SUMMARY_COUNT_STYLE = css`
  min-width: 45px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  font-size: ${pxToRem(47)};
  line-height: 1;
  font-weight: 700;
  color: #1d1d1d;
`;

export const SUMMARY_COUNT_MUTED_STYLE = css`
  ${SUMMARY_COUNT_STYLE}
  color: #a0a0a0;
`;

export const SUMMARY_COPY_STYLE = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 2px;
  align-self: center;
`;

export const SUMMARY_LABEL_STYLE = css`
  font-size: ${pxToRem(16)};
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  line-height: 1.1;
  margin: 0;
`;

export const SUMMARY_LABEL_MUTED_STYLE = css`
  ${SUMMARY_LABEL_STYLE}
  color: #a0a0a0;
`;

export const SUMMARY_NOTE_STYLE = css`
  font-size: ${pxToRem(14)};
  color: #1d1d1d;
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.1;
  margin: 0;
`;

export const SUMMARY_NOTE_RED_STYLE = css`
  ${SUMMARY_NOTE_STYLE}
  color: #b82c2c;
  font-weight: 700;
`;

export const SUMMARY_NOTE_MUTED_STYLE = css`
  ${SUMMARY_NOTE_STYLE}
  color: #a0a0a0;
`;

export const NOTE_DOT_STYLE = css`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: currentColor;
`;

export const SECTION_HEADER_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;

  @media (max-width: 760px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const SECTION_TITLE_STYLE = css`
  font-size: ${pxToRem(13)};
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #3c3a37;
`;

export const SORT_WRAPPER_STYLE = css`
  position: relative;
  width: 187px;

  @media (max-width: 760px) {
    width: 100%;
  }
`;

export const CARD_LIST_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const PROJECT_CARD_STYLE = css`
  display: flex;
  align-items: stretch;
  gap: 20px;
  border: 1px solid #ddd6cb;
  border-radius: 0;
  background: #ffffff;
  padding: 10px 14px;
  cursor: pointer;

  &:hover {
    border-color: #c7bfae;
  }

  @media (max-width: 1280px) {
    gap: 16px;
  }

  @media (max-width: 1040px) {
    align-items: flex-start;
  }

  @media (max-width: 760px) {
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }
`;

export const PROJECT_CARD_CLOSED_STYLE = css`
  ${PROJECT_CARD_STYLE}
`;

export const PROJECT_MAIN_STYLE = css`
  min-width: 0;
  flex: 1;
  display: flex;
  gap: 20px;
  align-items: stretch;
  align-self: stretch;

  @media (max-width: 920px) {
    gap: 16px;
  }

  @media (max-width: 760px) {
    width: 100%;
    gap: 12px;
  }
`;

export const PROJECT_IMAGE_STYLE = css`
  width: 132px;
  height: 100px;
  margin: 0;
  border-radius: 0;
  object-fit: cover;
  background: #dfdfdf;
  align-self: stretch;

  @media (max-width: 920px) {
    width: 132px;
    height: 92px;
  }

  @media (max-width: 760px) {
    width: 112px;
    height: 88px;
    margin: 0;
    border-radius: 2px;
  }
`;

export const PROJECT_DETAILS_STYLE = css`
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
`;

export const TAG_ROW_STYLE = css`
  display: flex;
  align-items: center;
  gap: 0;
  flex-wrap: nowrap;
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
`;

export const PROJECT_ADDRESS_STYLE = css`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  font-size: ${pxToRem(14)};
  font-weight: 700;
  min-width: 0;
`;

export const PROJECT_ADDRESS_MUTED_STYLE = css`
  ${PROJECT_ADDRESS_STYLE}
  color: #a0a0a0;
`;

export const ADDRESS_DOT_STYLE = css`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #b82c2c;
  flex-shrink: 0;
  margin-top: 4px;
  display: none;
`;

export const ADDRESS_TEXT_STYLE = css`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PROJECT_FACTS_STYLE = css`
  font-size: ${pxToRem(12)};
  color: #69635b;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const PROJECT_META_STYLE = css`
  min-width: 0;

  @media (max-width: 760px) {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
`;

export const PROJECT_META_LABEL_STYLE = css`
  font-size: ${pxToRem(12)};
  font-weight: 500;
  color: #58534c;
  margin-bottom: 4px;
`;

export const PROJECT_META_VALUE_STYLE = css`
  font-size: ${pxToRem(14)};
  color: #2c2c2c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 760px) {
    white-space: normal;
  }
`;

export const PROJECT_META_VALUE_MUTED_STYLE = css`
  ${PROJECT_META_VALUE_STYLE}
  color: #a0a0a0;
`;

export const PROJECT_META_VALUE_DUE_ALERT_STYLE = css`
  ${PROJECT_META_VALUE_STYLE}
  color: #b82c2c;
  font-weight: 700;
`;

export const PROJECT_META_VALUE_DUE_ALERT_ROW_STYLE = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const PROJECT_DUE_ALERT_DOT_STYLE = css`
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: #a01c1c;
  flex-shrink: 0;
`;

export const PROJECT_META_VALUE_STRONG_STYLE = css`
  ${PROJECT_META_VALUE_STYLE}
  font-weight: 700;
`;

export const AMOUNT_STYLE = css`
  font-size: ${pxToRem(15)};
  font-weight: 700;
`;

export const ROW_ACTIONS_STYLE = css`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-self: end;

  @media (max-width: 920px) {
    justify-self: start;
  }

  @media (max-width: 760px) {
    grid-column: 1 / -1;
    width: 100%;
    justify-content: flex-start;
  }
`;

export const ROW_ACTION_BUTTON_STYLE = css`
  height: 32px;
  min-width: 88px;
  padding: 0 14px;
  border-radius: 5px;
  border: 1px solid #6b6b6b;
  background: #ffffff;
  color: #6b6b6b;
  font-size: ${pxToRem(12)};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: pointer;
`;

export const ROW_ACTION_PRIMARY_BUTTON_STYLE = css`
  ${ROW_ACTION_BUTTON_STYLE}
  border-color: #1d1d1d;
  color: #1d1d1d;
`;

export const TYPE_TAG_STYLE = css`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 22px;
  padding: 0 9px;
  border-radius: 0;
  border: 1px solid #6b6b6b;
  background: #6b6b6b;
  color: #ffffff;
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  line-height: 1;
`;

export const ARTIFACT_TAG_STYLE = css`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 22px;
  padding: 0 9px;
  border-radius: 0;
  border: 1px solid #1d1d1d;
  background: #1d1d1d;
  color: #ffffff;
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  line-height: 1;
`;

export const PROJECT_STATIC_TAG_STYLE = css`
  ${ARTIFACT_TAG_STYLE}
  border-color: #6b6b6b;
  background: #6b6b6b;
`;

export const STATUS_TAG_STYLE = css`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 22px;
  padding: 0 9px;
  border-radius: 0;
  border: 1px solid currentColor;
  background: #ffffff;
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  line-height: 1;
`;

export const STATE_TAG_MUTED_STYLE = css`
  ${STATUS_TAG_STYLE}
  color: #a0a0a0;
`;

export const PROJECT_NAME_STYLE = css`
  ${PROJECT_META_VALUE_STRONG_STYLE}
  white-space: nowrap;
`;

export const ASSIGNEE_VALUE_STYLE = css`
  ${PROJECT_META_VALUE_STRONG_STYLE}
  overflow: visible;
`;

export const ASSIGNEE_TOOLTIP_TRIGGER_STYLE = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 0;

  &:hover > div:last-of-type {
    opacity: 1;
    transform: translate(-50%, 0);
    pointer-events: auto;
  }
`;

export const ASSIGNEE_TOOLTIP_STYLE = css`
  position: absolute;
  left: 50%;
  top: calc(100% + 12px);
  transform: translate(-50%, 6px);
  min-width: 180px;
  max-width: 220px;
  background: #1d1d15;
  color: #ffffff;
  border-radius: 5px;
  padding: 14px 16px 12px;
  font-size: ${pxToRem(12)};
  line-height: 1.35;
  text-align: center;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 120ms ease,
    transform 120ms ease;
  z-index: 20;
`;

export const ASSIGNEE_TOOLTIP_CARET_STYLE = css`
  position: absolute;
  left: 50%;
  top: -7px;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: 8px solid #1d1d15;
`;

export const PROJECT_META_ROW_STYLE = css`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 12px;
  margin-top: 10px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    row-gap: 12px;
  }

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 10px;
  }
`;

export const PROJECT_ACTION_MENU_STYLE = css`
  width: 28px;
  min-width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1d1d1d;
  align-self: center;
  position: relative;

  @media (max-width: 760px) {
    align-self: flex-end;
  }
`;

export const PROJECT_ACTION_MENU_BUTTON_STYLE = css`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: inherit;
`;

export const PROJECT_ACTION_MENU_PANEL_STYLE = css`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 140px;
  background: #ffffff;
  border: 1px solid #e6e0d7;
  border-radius: 6px;
  box-shadow: 0 10px 24px rgba(29, 29, 29, 0.14);
  padding: 0;
  overflow: hidden;
  z-index: 25;
`;

export const PROJECT_ACTION_MENU_ITEM_STYLE = css`
  width: 100%;
  border: none;
  background: transparent;
  padding: 11px 14px;
  text-align: left;
  font-size: ${pxToRem(14)};
  color: #1d1d1d;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #fff3b0;
  }
`;

export const EMPTY_STATE_STYLE = css`
  border: 1px dashed #d8d1c6;
  border-radius: 10px;
  background: #ffffff;
  color: #6d665c;
  padding: 32px 20px;
  text-align: center;
  font-size: ${pxToRem(14)};
`;

export const CAUGHT_UP_EMPTY_STATE_STYLE = css`
  flex: 1;
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
`;

export const CAUGHT_UP_CONTENT_STYLE = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 520px;
`;

export const CAUGHT_UP_ICON_STYLE = css`
  width: 64px;
  height: 64px;
  margin-bottom: 24px;
`;

export const CAUGHT_UP_TITLE_STYLE = css`
  font-size: ${pxToRem(24)};
  font-weight: 500;
  color: #2b2b2b;
  margin-bottom: 16px;
`;

export const CAUGHT_UP_BODY_STYLE = css`
  font-size: ${pxToRem(16)};
  line-height: 1.45;
  color: #2b2b2b;
`;

export const CAUGHT_UP_BODY_EMPHASIS_STYLE = css`
  font-weight: 700;
`;

export const LOAD_MORE_STYLE = css`
  margin: 16px auto 0;
  border: none;
  background: transparent;
  color: #2d88e6;
  font-size: ${pxToRem(13)};
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  display: block;
  text-align: center;
`;

export const OFFLINE_STYLE = css`
  padding: 12px 18px;
  font-size: ${pxToRem(13)};
  color: #8b6d1d;
  background: #fff4cf;
  border-bottom: 1px solid #eedc9b;
`;

export const SIDEBAR_MARKETS_LIST_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

export const SIDEBAR_ADD_MARKET_BUTTON_STYLE = css`
  margin-top: 18px;
  align-self: center;
  height: 40px;
  width: 138px;
  border-radius: 5px;
  border: 1px solid #1d1d15;
  background: #ffffff;
  color: #1d1d15;
  font-size: ${pxToRem(12)};
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const ADD_MARKETS_BODY_STYLE = css`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`;

export const ADD_MARKETS_DRAWER_STYLE = css`
  width: 340px;

  > div:first-of-type {
    min-height: 76px;
    align-items: flex-start;
    padding-top: 18px;
  }

  > div:first-of-type > div:first-of-type {
    gap: 2px;
  }

  > div:first-of-type > div:first-of-type > span {
    line-height: 1.2;
  }
`;

export const ADD_MARKETS_TOP_ROW_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px 10px;
`;

export const ADD_MARKETS_COUNT_STYLE = css`
  ${KAI_SHIKI_TYPOGRAPHY.smallBold}
  color: #1d1d1d;

  span {
    font-weight: 400;
    color: #5c5c5c;
  }
`;

export const ADD_MARKETS_SELECT_ALL_STYLE = css`
  border: none;
  background: transparent;
  color: #2b78d4;
  font-size: ${pxToRem(12)};
  font-weight: 700;
  cursor: pointer;
  padding: 0;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

export const ADD_MARKETS_LIST_STYLE = css`
  flex: 1;
  overflow-y: auto;
  padding: 0 18px;
`;

export const ADD_MARKETS_GROUP_STYLE = css`
  padding: 14px 0 4px;
`;

export const ADD_MARKETS_GROUP_LABEL_STYLE = css`
  font-size: ${pxToRem(13)};
  color: #7b7b7b;
  margin-bottom: 8px;
`;

export const ADD_MARKETS_ROW_STYLE = css`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 36px;
  cursor: pointer;
`;

export const ADD_MARKETS_CHECKBOX_INPUT_STYLE = css`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

export const ADD_MARKETS_CHECKBOX_STYLE = css`
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1px solid #969696;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const ADD_MARKETS_CHECKBOX_SELECTED_STYLE = css`
  ${ADD_MARKETS_CHECKBOX_STYLE}
  border-color: #41b36e;
  background: #41b36e;
  color: #ffffff;
`;

export const ADD_MARKETS_CHECKBOX_TICK_STYLE = css`
  font-size: ${pxToRem(12)};
  font-weight: 700;
  line-height: 1;
`;

export const ADD_MARKETS_ROW_LABEL_STYLE = css`
  font-size: ${pxToRem(14)};
  color: #1d1d1d;
`;

export const ADD_MARKETS_FOOTER_STYLE = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 18px 18px 20px;
  background: #ffffff;
`;

export const ADD_MARKETS_FOOTER_BUTTON_ROW_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  width: 100%;
`;

export const ADD_MARKETS_CANCEL_BUTTON_STYLE = css`
  height: 32px;
  min-width: 140px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1px solid #1d1d1d;
  background: #ffffff;
  color: #1d1d1d;
  font-size: ${pxToRem(12)};
  font-weight: 700;
  cursor: pointer;
`;

export const ADD_MARKETS_SAVE_BUTTON_STYLE = css`
  height: 32px;
  min-width: 140px;
  padding: 0 18px;
  border-radius: 6px;
  border: 1px solid #48b66f;
  background: #48b66f;
  color: #ffffff;
  font-size: ${pxToRem(12)};
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;
